import type { ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useLogoutMutation } from '@/modules/auth/mutations/useLogoutMutation';
import { HMLoadError } from '@/modules/hiring-manager/components/HMLoadError';
import { getHMTenantSelection } from '@/modules/hiring-manager/utils/tenant';
import { useAcceptTermsMutation } from '@/modules/legal/mutations/useAcceptTermsMutation';
import { useTermsAcceptanceQuery } from '@/modules/legal/queries/useTermsAcceptanceQuery';
import { SignupFlowShell } from '@/modules/signup-flow/components/layout/SignupFlowShell';
import Loader from '@/modules/ui/components/loader/Loader';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

import { AcceptTermsScreen } from './accept-terms/AcceptTermsScreen';

interface TermsGateProps {
  children: ReactNode;
}

/**
 * Blocks the workspace until the user has accepted the current Terms &
 * Conditions for the selected tenant. The API decides who is gated (Hiring
 * Managers, not staff) and closes the gate again when the terms change.
 * It fails closed: if the check cannot load, the user gets a retry, not the app.
 */
export function TermsGate({ children }: TermsGateProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUserQuery = useCurrentUserQuery();
  const tenantId = getHMTenantSelection(
    currentUserQuery.data,
    searchParams,
  ).selectedTenantId;
  const termsQuery = useTermsAcceptanceQuery(tenantId);
  const acceptMutation = useAcceptTermsMutation();
  const logoutMutation = useLogoutMutation();

  if (termsQuery.isPending) {
    return <Loader />;
  }

  if (termsQuery.isError || !termsQuery.data) {
    return (
      <SignupFlowShell>
        <HMLoadError
          description="We could not check your workspace terms. Check your connection and try again."
          isRetrying={termsQuery.isFetching}
          onRetry={() => void termsQuery.refetch()}
          title="Something went wrong"
        />
      </SignupFlowShell>
    );
  }

  if (!termsQuery.data.isRequired || !tenantId) {
    return children;
  }

  return (
    <AcceptTermsScreen
      error={
        acceptMutation.isError
          ? actionErrorMessage('record your acceptance', acceptMutation.error)
          : null
      }
      isAccepting={acceptMutation.isPending}
      isLoggingOut={logoutMutation.isPending}
      onAccept={() => acceptMutation.mutate(tenantId)}
      onLogout={() =>
        logoutMutation.mutate(undefined, {
          onSuccess: () => navigate('/login', { replace: true }),
        })
      }
      terms={termsQuery.data.terms}
    />
  );
}
